import "../../components/EventUserDashboard.css";

import React, { useEffect, useMemo, useState } from "react";
import { Select } from "@mantine/core";
import { getAuthHeaders } from "./../../api/authHelper";

/* MATCH BACKEND ENUM */
enum Bar {
    BAR1 = 1,
    BAR2 = 2,
    BAR3 = 3
}

interface EventDto {
    eventID: number;
    eventName?: string;
}

interface FunctionDto {
    functionId: number;
    functionName: string;
}

interface FunctionAllowanceDto {
    bar: number;
    maxDrinks: number;
    validDate?: string;
}

const getBarName = (bar: number) => {
    switch (bar) {
        case Bar.BAR1:
            return "VIP Lounge";
        case Bar.BAR2:
            return "Hygge Lounge";
        case Bar.BAR3:
            return "Jail House Bar";
        default:
            return "Unknown";
    }
};

const toArray = <T,>(result: any): T[] => {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result?.items)) return result.items;
    if (Array.isArray(result?.functions)) return result.functions;
    if (Array.isArray(result?.results)) return result.results;
    if (Array.isArray(result?.value)) return result.value;
    if (Array.isArray(result?.events)) return result.events;
    return [];
};

const toNonNegativeNumber = (value: string | number): number => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) return 0;
    return Math.floor(num);
};

const normalizeEvent = (x: any): EventDto => ({
    eventID: Number(
        x?.eventID ??
        x?.EventID ??
        x?.eventId ??
        x?.EventId ??
        x?.id ??
        0
    ),
    eventName:
        x?.eventName ??
        x?.EventName ??
        x?.name ??
        x?.Name ??
        ""
});

const normalizeFunction = (x: any): FunctionDto => ({
    functionId: Number(
        x?.functionId ??
        x?.FunctionId ??
        x?.id ??
        x?.Id ??
        0
    ),
    functionName:
        x?.functionName ??
        x?.FunctionName ??
        x?.name ??
        x?.Name ??
        ""
});

const getTodayString = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

const AdminDrinkFunctionAllowance: React.FC = () => {
    const apiUrl = process.env.REACT_APP_API_URL || "";

    const [events, setEvents] = useState<EventDto[]>([]);
    const [functions, setFunctions] = useState<FunctionDto[]>([]);

    const [eventId, setEventId] = useState<number>(0);
    const [eventName, setEventName] = useState<string>("");

    const [functionId, setFunctionId] = useState<number>(0);
    const [functionName, setFunctionName] = useState<string>("");

    const [validDate, setValidDate] = useState<string>(getTodayString());

    const [bar1Drinks, setBar1Drinks] = useState<number>(0);
    const [bar2Drinks, setBar2Drinks] = useState<number>(0);
    const [bar3Drinks, setBar3Drinks] = useState<number>(0);

    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const resetBars = () => {
        setBar1Drinks(0);
        setBar2Drinks(0);
        setBar3Drinks(0);
    };

    useEffect(() => {
        const loadEvents = async () => {
            try {
                const res = await fetch(`${apiUrl}/api/events/light`);
                if (!res.ok) throw new Error(`Could not fetch events (${res.status})`);

                const result = await res.json();
                const data = toArray<any>(result)
                    .map(normalizeEvent)
                    .filter((e) => e.eventID > 0)
                    .sort((a, b) =>
                        (a.eventName || `Event ${a.eventID}`).localeCompare(
                            b.eventName || `Event ${b.eventID}`,
                            "da"
                        )
                    );

                setEvents(data);
            } catch (err) {
                console.error(err);
                setError("Could not fetch events");
            }
        };

        loadEvents();
    }, [apiUrl]);

    const loadFunctionAllowances = async (
        selectedFunctionId: number,
        currentEventId: number,
        selectedDate: string,
        keepMessage = false
    ) => {
        resetBars();

        if (!keepMessage) {
            setMessage(null);
        }

        setError(null);

        const selected = functions.find((f) => f.functionId === selectedFunctionId);
        setFunctionName(selected?.functionName || "");

        if (!selectedFunctionId || !currentEventId || !selectedDate) return;

        try {
            const res = await fetch(
                `${apiUrl}/api/admin/drinks-function?functionId=${selectedFunctionId}&eventId=${currentEventId}&validDate=${encodeURIComponent(selectedDate)}`
            );

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || `Could not fetch drinks (${res.status})`);
            }

            const result = await res.json();
            const data = toArray<FunctionAllowanceDto>(result);

            data.forEach((item) => {
                if (item.bar === Bar.BAR1) setBar1Drinks(toNonNegativeNumber(item.maxDrinks));
                if (item.bar === Bar.BAR2) setBar2Drinks(toNonNegativeNumber(item.maxDrinks));
                if (item.bar === Bar.BAR3) setBar3Drinks(toNonNegativeNumber(item.maxDrinks));
            });
        } catch (err: any) {
            console.error(err);
            setError(err?.message || "Could not fetch drinks");
        }
    };

    const handleEventChange = async (id: number) => {
        setEventId(id);
        setFunctionId(0);
        setFunctionName("");
        setFunctions([]);
        resetBars();
        setMessage(null);
        setError(null);

        if (!id) {
            setEventName("");
            return;
        }

        const selected = events.find((e) => e.eventID === id);
        setEventName(selected?.eventName || `Event ${id}`);

        try {
            const res = await fetch(`${apiUrl}/api/events/${id}`, {
                headers: getAuthHeaders(),
            });

            if (!res.ok) {
                throw new Error(`Could not fetch event (${res.status})`);
            }

            const result = await res.json();
            const functionSource = result?.functions ?? result;

            const data = toArray<any>(functionSource)
                .map(normalizeFunction)
                .filter((f) => f.functionId > 0)
                .sort((a, b) => a.functionName.localeCompare(b.functionName, "da"));

            setFunctions(data);
        } catch (err) {
            console.error(err);
            setError("Could not fetch functions");
            setFunctions([]);
        }
    };

    const handleFunctionChange = async (id: number) => {
        setFunctionId(id);

        if (!id) {
            setFunctionName("");
            resetBars();
            setMessage(null);
            setError(null);
            return;
        }

        await loadFunctionAllowances(id, eventId, validDate, false);
    };

    const handleValidDateChange = async (value: string) => {
        setValidDate(value);
        setMessage(null);
        setError(null);

        if (functionId > 0 && eventId > 0 && value) {
            await loadFunctionAllowances(functionId, eventId, value, false);
        } else {
            resetBars();
        }
    };

    const functionSelectData = useMemo(() => {
        return functions.map((f) => ({
            value: String(f.functionId),
            label: f.functionName
        }));
    }, [functions]);

    const submit = async () => {
        if (!eventId || !functionId) {
            setError("Event and Function must be selected");
            return;
        }

        if (!validDate) {
            setError("Valid date must be selected");
            return;
        }

        setMessage(null);
        setError(null);
        setIsSaving(true);

        const bars = [
            { bar: Bar.BAR1, maxDrinks: toNonNegativeNumber(bar1Drinks) },
            { bar: Bar.BAR2, maxDrinks: toNonNegativeNumber(bar2Drinks) },
            { bar: Bar.BAR3, maxDrinks: toNonNegativeNumber(bar3Drinks) }
        ];

        try {
            for (const b of bars) {
                const res = await fetch(`${apiUrl}/api/admin/drinks-function`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        functionId,
                        eventId,
                        bar: b.bar,
                        maxDrinks: b.maxDrinks,
                        validDate
                    })
                });

                if (!res.ok) {
                    const text = await res.text();
                    throw new Error(
                        text || `Error while saving for ${getBarName(b.bar)} (${res.status})`
                    );
                }
            }

            await loadFunctionAllowances(functionId, eventId, validDate, true);
            setMessage("Drinks saved for all bars");
        } catch (err: any) {
            console.error(err);
            setError(err?.message || "Error while saving");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="dashContainer">
            <div className="wrapper">
                <div className="top">
                    <div className="name">
                        {eventName || "Select event"}
                    </div>
                    <div className="meta">
                        {functionName
                            ? `${functionName} - Adjust drinks per bar`
                            : "Select event, function and date"}
                    </div>
                </div>

                <div className="content">
                    {error && (
                        <div className="badgeRed" style={{ padding: 8, marginBottom: 16 }}>
                            {error}
                        </div>
                    )}

                    {message && (
                        <div className="statusBadge" style={{ padding: 8, marginBottom: 16 }}>
                            {message}
                        </div>
                    )}

                    <div className="sectionTitle">Selection</div>

                    <div className="statsGrid3" style={{ marginTop: 12 }}>
                        <div className="stat" style={{ padding: 16 }}>
                            <label>Event</label>
                            <Select
                                placeholder="Select event"
                                value={eventId ? String(eventId) : null}
                                onChange={(val) => handleEventChange(val ? Number(val) : 0)}
                                data={events.map((e) => ({
                                    value: String(e.eventID),
                                    label: e.eventName || `Event ${e.eventID}`
                                }))}
                                clearable
                                searchable
                                nothingFoundMessage="No events found"
                            />
                        </div>

                        {eventId > 0 && (
                            <div className="stat" style={{ padding: 16 }}>
                                <label>Function</label>
                                <Select
                                    placeholder="Select function"
                                    value={functionId ? String(functionId) : null}
                                    onChange={(val) => handleFunctionChange(val ? Number(val) : 0)}
                                    data={functionSelectData}
                                    clearable
                                    searchable
                                    nothingFoundMessage="No functions found"
                                />
                            </div>
                        )}

                        <div className="stat" style={{ padding: 16 }}>
                            <label>Valid date</label>
                            <input
                                type="date"
                                value={validDate}
                                onChange={(e) => handleValidDateChange(e.target.value)}
                            />
                        </div>
                    </div>

                    {functionId > 0 && validDate && (
                        <>
                            <div className="sectionTitle" style={{ marginTop: 30 }}>
                                Drinks per bar
                            </div>

                            <div className="statsGrid3" style={{ marginTop: 12 }}>
                                <div className="stat" style={{ padding: 16 }}>
                                    <label>{getBarName(Bar.BAR1)}</label>
                                    <input
                                        type="number"
                                        min={0}
                                        value={bar1Drinks}
                                        onChange={(e) => setBar1Drinks(toNonNegativeNumber(e.target.value))}
                                    />
                                </div>

                                <div className="stat" style={{ padding: 16 }}>
                                    <label>{getBarName(Bar.BAR2)}</label>
                                    <input
                                        type="number"
                                        min={0}
                                        value={bar2Drinks}
                                        onChange={(e) => setBar2Drinks(toNonNegativeNumber(e.target.value))}
                                    />
                                </div>

                                <div className="stat" style={{ padding: 16 }}>
                                    <label>{getBarName(Bar.BAR3)}</label>
                                    <input
                                        type="number"
                                        min={0}
                                        value={bar3Drinks}
                                        onChange={(e) => setBar3Drinks(toNonNegativeNumber(e.target.value))}
                                    />
                                </div>
                            </div>

                            <button
                                type="button"
                                style={{
                                    marginTop: 20,
                                    width: "100%",
                                    opacity: isSaving ? 0.7 : 1,
                                    cursor: isSaving ? "not-allowed" : "pointer"
                                }}
                                onClick={submit}
                                disabled={isSaving}
                            >
                                {isSaving ? "Saving..." : "Save changes"}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminDrinkFunctionAllowance;